import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import { api, withSyncPendingRetry } from "../api/client";
import { sseBus } from "../api/events";
import type { CloseResult } from "../api/types";
import { ActionStatus } from "../components/ActionStatus";
import { explorerAddress } from "../env";
import { formatRcd, formatSol } from "../overlay/format";
import { useWalletAction } from "../overlay/useWalletAction";
import { usePlayer } from "../state/PlayerContext";
import { t } from "../strings";
import { signBuilt } from "../wallet/actions";
import { bytesToBase64, mergeSignatures } from "../wallet/tx";
import { useWallet } from "../wallet/WalletProvider";
import { connectWallet, externalGameWallet, signInWithSolana } from "../wallet/walletStandard";

function VaultQr({ address }: { address: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    QRCode.toDataURL(`solana:${address}`, { margin: 1, width: 192 })
      .then(setSrc)
      .catch(() => setSrc(null));
  }, [address]);
  return src ? <img className="qr" src={src} alt={`QR code for ${address}`} width={192} height={192} /> : null;
}

function MigrationWizard() {
  const { wallet, externalWallets } = useWallet();
  const [step, setStep] = useState<"idle" | "working" | "done">("idle");
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ moved: number; total: number } | null>(null);

  useEffect(() => sseBus.on("migration.progress", (ev) => setProgress({ moved: ev.moved, total: ev.total })), []);

  const start = async (name: string) => {
    if (!wallet) return;
    setStep("working");
    setError(null);
    try {
      setStatus(`Connect ${name} and sign in to prove you control the new wallet…`);
      const conn = await connectWallet(name);
      if (conn.account.address === wallet.address) throw new Error("Choose a different wallet from the one you're logged in with.");
      const proof = await signInWithSolana(conn.wallet, conn.account);
      setStatus("Preparing the migration…");
      const built = await withSyncPendingRetry(
        () => api.migrate(conn.account.address, proof),
        (w) => w && setStatus("Waiting for your last save to finish…"),
      );
      setStatus("Sign with your current wallet…");
      const first = await signBuilt(wallet, built);
      setStatus(`Sign with ${name}…`);
      const second = await externalGameWallet(conn.wallet, conn.account).signTransaction(first.signed);
      const both = mergeSignatures(first.signed, second);
      setStatus("Submitting…");
      await api.migrateSubmit(bytesToBase64(both));
      setStatus("Migration started. Your Pokémon move over the next few minutes; log in with the new wallet when it finishes.");
      setStep("done");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setStep("idle");
      setStatus(null);
    }
  };

  return (
    <section className="card">
      <h2>Move to a new wallet</h2>
      <p>
        Migration moves your player account, vault, in-game ₽, rare items and every Pokémon to a new wallet. Both wallets sign. Claimed RCD, item tokens and
        Master Balls in your current wallet are yours to send over yourself.
      </p>
      <p className="banner warn">{t.lostWallet}</p>
      {step === "idle" &&
        (externalWallets.length ? (
          <div className="wallets">
            {externalWallets.map((w) => (
              <button key={w.name} onClick={() => void start(w.name)}>
                <img src={w.icon} alt="" width={20} height={20} /> Move to {w.name}
              </button>
            ))}
          </div>
        ) : (
          <p className="hint">Install a Solana wallet (Phantom, Solflare, Backpack…) to migrate.</p>
        ))}
      {status && <p className="status">{status}</p>}
      {progress && (
        <p>
          Moved {progress.moved} of {progress.total} <progress max={progress.total || 1} value={progress.moved} />
        </p>
      )}
      {error && <p className="status error">{error}</p>}
    </section>
  );
}

function CloseAccount() {
  const [confirmForfeit, setConfirmForfeit] = useState(false);
  const [check, setCheck] = useState<CloseResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const action = useWalletAction();

  const review = async () => {
    setError(null);
    try {
      setCheck(await api.close(confirmForfeit));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const submit = async () => {
    if (!window.confirm("Close your RedChain account? This can't be undone.")) return;
    await action.run(() => api.close(confirmForfeit));
  };

  return (
    <section className="card">
      <h2>Close account</h2>
      <p>Closing returns your player account's deposits to your wallet. Everything that would be lost must be cleared first.</p>
      <label className="inline">
        <input type="checkbox" checked={confirmForfeit} onChange={(e) => setConfirmForfeit(e.target.checked)} /> I accept forfeiting any remaining in-game ₽ or
        item credit
      </label>
      <div className="row">
        <button onClick={review}>Check what's left</button>
        {check && check.blockers.length === 0 && (
          <button className="danger" disabled={action.busy} onClick={submit}>
            Close account{check.refundLamports ? ` and get back ${formatSol(check.refundLamports)} SOL` : ""}
          </button>
        )}
      </div>
      {check && check.blockers.length > 0 && (
        <>
          <p>Before you can close:</p>
          <ul>
            {check.blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </>
      )}
      {error && <p className="status error">{error}</p>}
      <ActionStatus {...action} />
    </section>
  );
}

/** /profile — export key, vault + QR, migration, close account, ledger CSV (WEB-16). */
/** §15.5.5: whether releases in Bill's PC refund ₽ (default) or SOL. Burns choose per burn. */
function ReleaseRefunds({ releaseAs, rcdPerSol }: { releaseAs: "rcd" | "sol"; rcdPerSol: string }) {
  const action = useWalletAction();
  const { refresh } = usePlayer();
  const asRcd = releaseAs === "rcd";
  const toggle = async () => {
    if (await action.run(() => api.setRefundPref(asRcd ? "sol" : "rcd"))) await refresh();
  };
  return (
    <section className="card">
      <h2>Release refunds</h2>
      <p>{t.releasePref(asRcd)}</p>
      <p className="hint">Rate: {formatRcd(rcdPerSol)} per SOL.</p>
      <button disabled={action.busy} onClick={() => void toggle()}>
        {asRcd ? "Refund releases in SOL instead" : "Refund releases in ₽ instead"}
      </button>
      <ActionStatus {...action} />
    </section>
  );
}

export function Profile() {
  const { wallet, auth, logout } = useWallet();
  const { me } = usePlayer();
  const [csvError, setCsvError] = useState<string | null>(null);

  if (!auth || !wallet)
    return (
      <main className="page">
        <Link to="/">Log in</Link> to see your profile.
      </main>
    );

  const downloadCsv = async () => {
    setCsvError(null);
    try {
      const blob = await api.ledgerCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `redchain-ledger-${wallet.address.slice(0, 8)}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) {
      setCsvError(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <main className="page profile">
      <section className="card">
        <h2>Wallet</h2>
        <p>
          <code>{wallet.address}</code>{" "}
          <a href={explorerAddress(wallet.address)} target="_blank" rel="noreferrer">
            explorer
          </a>
        </p>
        <p>{wallet.kind === "custodial" ? "Custody game account" : "Connected wallet"}</p>
        {me && (
          <dl className="kv">
            <dt>Trainer</dt>
            <dd>{me.trainerName ? `${me.trainerName} (${me.trainerId})` : "—"}</dd>
            <dt>Game</dt>
            <dd>{me.gameVersion ?? "—"}</dd>
            <dt>In-game ₽</dt>
            <dd>{formatRcd(me.gameBalance)}</dd>
            <dt>Pokémon on chain</dt>
            <dd>{me.monCount}</dd>
          </dl>
        )}
        <button onClick={() => void logout()}>Log out</button>
      </section>

      {me && (
        <section className="card">
          <h2>Vault</h2>
          <p>
            Send SOL to your vault to pay for Pokémon storage deposits. Balance: <strong>{formatSol(me.vault.lamports)} SOL</strong>, of which{" "}
            {formatSol(me.vault.refundableLockedLamports)} SOL is refundable from Pokémon you own.
          </p>
          <p>
            <code>{me.vault.address}</code>
          </p>
          <VaultQr address={me.vault.address} />
          <p className="hint">A transfer must leave the vault at 0 or at least {formatSol(me.vault.minimum)} SOL.</p>
        </section>
      )}

      {me?.registered && me.refunds.rcdPerSol && <ReleaseRefunds releaseAs={me.refunds.releaseAs} rcdPerSol={me.refunds.rcdPerSol} />}

      <section className="card">
        <h2>Ledger</h2>
        <p>Every in-game credit and debit, claim, deposit, fee, transfer, mint and sale for your wallet, for your own records.</p>
        <button onClick={downloadCsv}>Download CSV</button>
        {csvError && <p className="status error">{csvError}</p>}
      </section>

      <MigrationWizard />
      <CloseAccount />
    </main>
  );
}
